import { httpsCallable } from "firebase/functions";
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { functions } from "../firebase";

export function ActivateAccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [successRole, setSuccessRole] = useState<string>("parent");

  const missingRequirements = [
    password.length >= 8 ? null : "at least 8 characters",
    /[A-Z]/.test(password) ? null : "an uppercase letter",
    /[a-z]/.test(password) ? null : "a lowercase letter",
    /\d/.test(password) ? null : "a number",
    /[^A-Za-z0-9]/.test(password) ? null : "a special character",
  ].filter(Boolean) as string[];

  const canSubmit = !!token && missingRequirements.length === 0 && password === confirmPassword && !busy;
  const showLiveHelp = password.length > 0 || confirmPassword.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError("This activation link is missing its token. Please use the latest email from the school office.");
      return;
    }

    if (missingRequirements.length) {
      setError(`Password must include ${missingRequirements.join(", ")}.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const fn = httpsCallable(functions, "completeAccountActivation");
      const res: any = await fn({ token, password });
      setSuccessEmail(String(res?.data?.email || ""));
      setSuccessRole(String(res?.data?.role || "parent"));
    } catch (err: any) {
      setError(err?.message || "This activation link is invalid or expired. Please request a fresh setup email.");
    } finally {
      setBusy(false);
    }
  }

  if (successEmail) {
    const isParent = successRole === "parent";
    return (
      <div className="center">
        <div className="card" style={{ maxWidth: 560 }}>
          <h2>Account Activated</h2>
          <p className="muted">
            Your password has been set. You can now sign in with {successEmail}.
          </p>
          {isParent ? (
            <p className="muted">
              Please open the Zion Classical Academy mobile app and sign in there. Parent and family accounts do not use Admin Web.
            </p>
          ) : (
            <button className="btn" onClick={() => navigate(`/login?email=${encodeURIComponent(successEmail)}`, { replace: true })}>
              Continue to Admin Web Sign In
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 560 }}>
        <h2>Activate Account</h2>
        <p className="muted">
          Set your password to finish account setup. Activation links expire after 7 days and can only be used once.
        </p>

        {!token ? (
          <div className="error">This activation link is missing its token. Please use the latest setup email.</div>
        ) : null}

        {error ? <div className="error">{error}</div> : null}

        <form onSubmit={submit} className="form">
          <label className="label">New Password</label>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <div className="muted" style={{ fontSize: 13, display: "grid", gap: 6 }}>
            <div>Use at least 8 characters with uppercase, lowercase, a number, and a special character.</div>
            {showLiveHelp ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li style={{ color: password.length >= 8 ? "#166534" : "#b45309" }}>At least 8 characters</li>
                <li style={{ color: /[A-Z]/.test(password) ? "#166534" : "#b45309" }}>An uppercase letter</li>
                <li style={{ color: /[a-z]/.test(password) ? "#166534" : "#b45309" }}>A lowercase letter</li>
                <li style={{ color: /\d/.test(password) ? "#166534" : "#b45309" }}>A number</li>
                <li style={{ color: /[^A-Za-z0-9]/.test(password) ? "#166534" : "#b45309" }}>A special character</li>
              </ul>
            ) : null}
          </div>

          <label className="label">Confirm Password</label>
          <input
            className="input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
          {showLiveHelp && confirmPassword && password !== confirmPassword ? (
            <div className="muted" style={{ fontSize: 13, color: "#b45309" }}>Passwords do not match yet.</div>
          ) : null}
          {showLiveHelp && !canSubmit ? (
            <div className="muted" style={{ fontSize: 13 }}>
              Finish the checklist above and make sure both password fields match before the button becomes active.
            </div>
          ) : null}

          <button className="btn" disabled={!canSubmit}>
            {busy ? "Activating..." : "Set Password"}
          </button>
        </form>

        <p className="muted" style={{ marginTop: 16 }}>
          Already activated? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
